import { instanceId } from './sessionInstance';
// Web Locks also prevent two tabs sharing the same HttpOnly cookie from operating together.
import type { AccountUser } from '../types';
import { fetchMe,heartbeatSession } from './auth';
let started = false;
let enterForeground: (() => Promise<boolean>) | null = null;
let unlock: (() => void) | null = null;
let timer: ReturnType<typeof setInterval> | null = null;
export async function startSessionLifecycle(
  refresh: (user: AccountUser | null) => void,
  blocked: () => void,
): Promise<boolean> {
  if (started) return enterForeground ? enterForeground() : false;
  started = true;
  let foreground = false;
  async function claim(): Promise<boolean> {
    if (!navigator.locks) return true; // server instance leases still enforce exclusivity
    return new Promise(resolve => {
      void navigator.locks
        .request('chefi-active-app', { ifAvailable: true }, async lock => {
          if (!lock) {
            resolve(false);
            return;
          }
          await new Promise<void>(release => {
            unlock = release;
            resolve(true);
          });
        })
        .catch(() => resolve(false));
    });
  }
  async function heartbeat(resume=false) {
    if (!foreground || document.visibilityState !== 'visible') return;
    try {
      if(resume)refresh(await fetchMe());else await heartbeatSession();
    } catch (error) {
      if (error instanceof Error && error.message === 'session_active')
        blocked();
      if(error instanceof Error&&error.message==='not_signed_in')refresh(null);
    }
  }
  async function enter(): Promise<boolean> {
    if (foreground) return true;
    if (!(await claim())) {
      blocked();
      return false;
    }
    foreground = true;
    timer = setInterval(() => {
      void heartbeat();
    }, 20_000);
    return true;
  }
  function leave() {
    if (!foreground) return;
    foreground = false;
    if (timer) clearInterval(timer);
    timer = null;
    // same-origin beacon carries the HttpOnly cookie and Origin, even on page close.
    navigator.sendBeacon(
      `/api/auth/session/release?instance=${instanceId}`,
      new Blob(['{}'], { type: 'application/json' }),
    );
    unlock?.();
    unlock = null;
  }
  enterForeground = enter;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible')
      void enter().then(ok => {
        if (ok) void heartbeat(true);
      });
    else leave();
  });
  window.addEventListener('pagehide', leave);
  return document.visibilityState === 'visible' ? enter() : false;
}
